<template>
  <div class="ip-checker-page">
    <HeadNav selectedLanguage="en" page="tools" />

    <main>
      <section class="hero">
        <div class="hero-glow" aria-hidden="true" />
        <div class="container hero-inner">
          <h1>Your IP Address is {{ displayIp }}</h1>
          <p v-if="displayCountry" class="country-line">Country: {{ displayCountry }}</p>
          <router-link class="btn btn-primary" to="/download/">Use VPN Now</router-link>
        </div>
      </section>

      <section class="section">
        <div class="container prose">
          <h2>What is an IP Checker</h2>
          <p>An IP Checker is a simple online tool that shows your current IP address and basic details about your internet connection. Every time you go online, your device uses an IP address that helps websites know where your connection is coming from.</p>
        </div>
      </section>

      <section class="section section-tint">
        <div class="container prose">
          <h2>Why use this tool</h2>
          <p>Want to know what your internet connection looks like from the outside? This tool lets you quickly check your IP address, see your basic connection details, test your VPN, and troubleshoot common network issues.</p>
          <ul class="doc-list">
            <li><strong>Check your current IP address</strong></li>
            <li>See the public IP assigned to your connection.</li>
            <li><strong>Test your VPN connection</strong></li>
            <li>Confirm whether your IP changes after connecting to a VPN.</li>
            <li><strong>Troubleshoot network issues</strong></li>
            <li>Check whether your connection information matches your expectations.</li>
            <li><strong>Understand your online visibility</strong></li>
            <li>Learn what basic information websites may see about your connection.</li>
          </ul>
        </div>
      </section>

      <section class="section">
        <div class="container prose">
          <h2>FAQ</h2>
          <h3>Is checking my IP address safe?</h3>
          <p>Yes. Checking your own IP address is safe and does not reveal sensitive information by itself.</p>
          <h3>Why does my IP address change?</h3>
          <p>Your IP address may change when you reconnect to the internet, restart your router, switch networks, or use a VPN.</p>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
  name: 'IpChecker',
  components: {
    HeadNav,
  },
  data() {
    return {
      loading: true,
      ip: '',
      country: '',
    };
  },
  computed: {
    displayIp() {
      if (this.loading) return '…';
      return this.ip || 'unavailable';
    },
    displayCountry() {
      if (this.loading) return '';
      return this.country || '';
    },
  },
  mounted() {
    this.fetchIp();
  },
  methods: {
    async fetchIp() {
      this.loading = true;
      this.country = '';
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        if (!res.ok) throw new Error('Unable to detect IP');
        const data = await res.json();
        this.ip = data.ip || '';

        if (this.ip) {
          try {
            const geoRes = await fetch(`https://ipapi.co/${this.ip}/json/`);
            if (geoRes.ok) {
              const geo = await geoRes.json();
              this.country = geo.country_name || geo.country || '';
            }
          } catch (e) {
            // 国家查询失败时仍展示 IP
          }
        }
      } catch (e) {
        this.ip = '';
        this.country = '';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ip-checker-page {
  min-height: 70vh;
  background:
    radial-gradient(ellipse 80% 50% at 10% -10%, rgba(91, 79, 232, 0.12), transparent 55%),
    radial-gradient(ellipse 60% 40% at 90% 0%, rgba(59, 105, 255, 0.08), transparent 50%),
    #f5f4fb;
  color: var(--vpn-ink);
  font-family: var(--vpn-font);
}

.container {
  width: var(--vpn-container);
  margin: 0 auto;
}

.hero {
  position: relative;
  overflow: hidden;
  padding: 40px 0 28px;
}

.hero-glow {
  position: absolute;
  inset: auto -10% 20% auto;
  width: 360px;
  height: 360px;
  background: radial-gradient(circle, rgba(91, 79, 232, 0.16), transparent 70%);
  pointer-events: none;
}

.hero-inner {
  position: relative;
}

h1 {
  margin: 0 0 12px;
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.18;
  letter-spacing: -0.03em;
  font-weight: 800;
}

.country-line {
  margin: 0 0 20px;
  font-size: 17px;
  font-weight: 700;
  color: var(--vpn-ink-soft);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 800;
  text-decoration: none;
  border: 0;
  cursor: pointer;
  font-family: inherit;
}

.btn-primary {
  background: linear-gradient(135deg, var(--vpn-teal-bright), var(--vpn-teal));
  color: #fff;
  box-shadow: 0 12px 24px rgba(91, 79, 232, 0.28);
}

.section {
  padding: 28px 0;
}

.section:last-child {
  padding-bottom: 72px;
}

.section-tint {
  background: linear-gradient(180deg, rgba(91, 79, 232, 0.05), transparent);
}

.prose h2 {
  margin: 0 0 12px;
  font-size: clamp(24px, 3vw, 30px);
  font-weight: 800;
  letter-spacing: -0.02em;
}

.prose h3 {
  margin: 20px 0 8px;
  font-size: 17px;
  font-weight: 800;
}

.prose h3:first-of-type {
  margin-top: 4px;
}

.prose p {
  margin: 0;
  font-size: 16px;
  line-height: 1.75;
  color: var(--vpn-ink-soft);
}

.prose p + .doc-list {
  margin-top: 14px;
}

.doc-list {
  margin: 0;
  padding-left: 20px;
}

.doc-list li {
  margin: 0;
  font-size: 16px;
  line-height: 1.7;
  color: var(--vpn-ink-soft);
}

/* 标题行与说明行成组：说明紧跟标题，下一组标题拉开一点 */
.doc-list li:nth-child(odd) {
  margin-top: 12px;
  color: var(--vpn-ink);
}

.doc-list li:nth-child(1) {
  margin-top: 0;
}

.doc-list li:nth-child(even) {
  margin-top: 2px;
  margin-bottom: 2px;
}

.doc-list strong {
  color: var(--vpn-ink);
}

@media (max-width: 640px) {
  .hero {
    padding: 28px 0 20px;
  }

  .section {
    padding: 24px 0;
  }

  .section:last-child {
    padding-bottom: 56px;
  }

  .btn {
    width: 100%;
  }
}
</style>
